import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DescriptionIcon from '@mui/icons-material/Description';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Snackbar,
  Typography
} from '@mui/material';

import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { ROUTES } from 'routes/constants';
import { mascaraCpfCnpj } from 'utils/format_cpf';
import ActionButtons from './ActionButtons';
import ConfirmModal from './ConfirmModal';
import CpfField from './CpfField';
import useCuffaForm from './hooks/useCuffaForm';
import StatusBadge from './StatusBadge';

type Situacao = 'Novo' | 'Em andamento' | 'Finalizado' | 'Em coleta (Rascunho)' | 'Sincronizado' | 'Enviado ao Estado';

interface BoxCuffaFormularioProps {
  nomeSecretaria: string;
  situacao: Situacao;
  temProgresso: boolean;
  onContinuar: (uuidFromChild?: string) => void;
  onNovoFormulario: () => void;
  onEditar: () => void;
  onVisualizar: () => void;
}

const BoxCuffaFormulario: React.FC<BoxCuffaFormularioProps> = ({
  nomeSecretaria,
  onContinuar,
  onNovoFormulario
}) => {
  const navigate = useNavigate();
  const { cpf, handleCpfChange, handleVerificarCPF, loading,
    activeCollection, collectionUuid, handleEnviarColeta, openModal, setOpenModal, confirmData, confirmLoading,
    setConfirmData, setConfirmLoading, situacao, cpfError, cpfInfo } = useCuffaForm();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const isEmAndamento = situacao === 'Em andamento';

  const handleVisualizar = () => {
    if (activeCollection && activeCollection.uuid) {
      navigate(ROUTES.IDENTIFICACAO_FAMILIAR_CUFFA_VISUALIZAR.replace(':uuid', activeCollection.uuid));
    }
  };

  const handleValidarEEnviar = async () => {
    const uuid = collectionUuid ?? activeCollection?.uuid;
    if (!uuid) {
      alert('UUID da coleta não encontrado.');
      return;
    }


    setOpenModal(true);
    setConfirmLoading(true);
    setConfirmData(null);


    try {
      const res = await CuffaAPI.getDataByID(uuid);
      setConfirmData(res?.dados);
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Falha ao carregar os dados da coleta para confirmação.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 4,
        width: '100%',
        minHeight: 350,
        boxShadow: 3,
        borderRadius: 2,
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} flexGrow={1}>
        <Avatar sx={{ bgcolor: '#0d47a1', width: 56, height: 56 }}>
          <DescriptionIcon fontSize="large" />
        </Avatar>

        <Typography fontWeight="bold" fontSize={30} textAlign="center">
          {nomeSecretaria}
        </Typography>

        <Typography variant="subtitle1" textAlign="center">
          Situação: <StatusBadge situacao={situacao} />
        </Typography>

        {isEmAndamento && (
          <Alert severity="info" sx={{ width: '100%' }}>
            Você tem um formulário em progresso não finalizado.
          </Alert>
        )}
      </Box>

      {/* Campo CPF */}
      <CpfField value={mascaraCpfCnpj(cpf)} onChange={handleCpfChange} disabled={loading} />

      {cpfError && (
        <Alert severity="error" sx={{ width: '100%', marginTop: 2 }}>
          {cpfError}
        </Alert>
      )}

      {cpfInfo && (
        <Alert severity="info" sx={{ width: '100%', marginTop: 2 }}>
          {cpfInfo}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={(e) => {
          e.preventDefault();
          handleVerificarCPF();
        }}
        sx={{ marginTop: 2, height: 44 }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={22} /> : 'Verificar e Iniciar Coleta'}
      </Button>

      <ActionButtons
        situacao={situacao}
        activeCollection={activeCollection}
        onContinuar={(uuid) => onContinuar(uuid)}
        onNovoFormulario={onNovoFormulario}
        onValidarEEnviar={handleValidarEEnviar}
        onVisualizar={handleVisualizar}
      />

      <ConfirmModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        loading={confirmLoading}
        data={confirmData}
        onVisualizar={handleVisualizar}
        onConfirm={handleEnviarColeta}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default BoxCuffaFormulario;

